import { FC, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from '@tanstack/react-query';

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import Button from "@/components/Button";
import useAlerts from "@/hooks/useAlerts";
import { FormLayout, Input, Label, Larger, Textarea } from "@/components/Forms";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";
import { TeamResource, TopTopicsResource } from "@/types/resources";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { Dropdown, Option } from "@/components/Forms/Dropdown";
import useDidUpdate from "@/hooks/useDidUpdate";

type FormDataT = {
  title: string;
  team: Option | null;
  notes: string;
};

interface Props {
  onClose: () => void;
  topic: TopTopicsResource;
  category: string;
  topicsQueryKey: Array<any>;
}

const TopicEditModal: FC<Props> = ({
  onClose,
  topic,
  category,
  topicsQueryKey,
}) => {
  const queryClient = useQueryClient();

  const [teams, setTeams] = useState<Option[]>([]);

  useEffect(() => {
    api.get<never, TeamResource[]>("/v1/teams").then((teams) => {
      setTeams(
        teams.map((team) => {
          return { label: team.name, value: team.id.toString() };
        })
      );
    });
  }, []);

  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      title: topic.title || "",
      notes: topic.notes || "",
      team: null,
    },
  });

  useDidUpdate(() => {
    if (teams.length && topic.team) {
      const team = teams.find(
        (team) => team.value === topic.team!.id!.toString()
      )!;
      setValue("team", team);
    }
  }, [teams, setValue]);

  const alerts = useAlerts();
  const onSubmit = handleSubmit(async ({ notes, title, team }) => {
    try {
      await api.put<{ notes: string | null }, TopTopicsResource>(
        `/v1/video-topics/${topic.id}`,
        {
          ...(title !== topic.title && !!title && { title }),
          ...(!!team && { team_id: team.value }),
          notes,
          category,
        }
      );

      queryClient.refetchQueries(topicsQueryKey, { exact: true });
      alerts.success(`${topic.title} topic was successfully edited`);
      onClose();
    } catch (err) {
      handleFormErrors(err, setError, alerts);
    }
  });

  return (
    <Modal>
      <SidePanel heading={`Edit topic: ${topic.title}`} onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Label>Topic title</Label>
                <Input
                  {...register("title")}
                  placeholder={
                    category === "type"
                      ? "Add video type"
                      : `Add ${category} topic`
                  }
                  $hasError={!!errors.title}
                />
                {!!errors.title && (
                  <ValidationMessage>{errors.title.message}</ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="teamSelect">Team</Label>
                <Dropdown
                  // $hasError={!!errors.network}
                  options={teams}
                  control={control}
                  name="team"
                  id="teamSelect"
                />
              </Larger>
              <Larger>
                <Label>Note</Label>
                <Textarea {...register("notes")} placeholder="Add notes..." />
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default TopicEditModal;
